import { useEffect, useState, type FormEvent } from 'react';
import { Plus, Trash2, ArrowUp, ArrowDown, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { useCreateTicket, useUpdateTicket, type SaveTicketInput } from '@/hooks/useProduction';
import { useOrders } from '@/hooks/useSales';
import {
  STATION_LABELS, STATIONS, PRIORITY_LABELS,
  type Station, type Priority, type WorkTicket,
} from '@/types/production';

type Props = {
  open: boolean;
  onClose: () => void;
  ticket?: WorkTicket | null;
};

function today() { return new Date().toISOString().slice(0, 10); }

const EMPTY: SaveTicketInput = {
  status: 'draft',
  priority: 'normal',
  orderId: '',
  description: '',
  quantity: 1,
  unit: 'ea',
  dueDate: '',
  routing: [
    { station: 'panel_line' },
    { station: 'finish_line' },
    { station: 'qc' },
    { station: 'packaging' },
    { station: 'shipping' },
  ],
  currentStepIndex: 0,
  notes: '',
};

export function TicketFormModal({ open, onClose, ticket }: Props) {
  const { data: orders } = useOrders({ limit: 300 });
  const create = useCreateTicket();
  const update = useUpdateTicket();
  const editing = !!ticket;

  const [form, setForm] = useState<SaveTicketInput>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(ticket ? hydrate(ticket) : { ...EMPTY, dueDate: today() });
      setError(null);
    }
  }, [open, ticket]);

  function update_<K extends keyof SaveTicketInput>(key: K, val: SaveTicketInput[K]) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  function addStep() {
    update_('routing', [...form.routing, { station: 'qc' }]);
  }
  function removeStep(idx: number) {
    if (form.routing.length <= 1) return;
    update_('routing', form.routing.filter((_, i) => i !== idx));
  }
  function moveStep(idx: number, dir: -1 | 1) {
    const next = [...form.routing];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    update_('routing', next);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.orderId) { setError('Select a source order.'); return; }
    if (form.routing.length === 0) { setError('Add at least one routing step.'); return; }

    try {
      if (editing && ticket) {
        await update.mutateAsync({ id: ticket.id, ...form });
        toast.success(`Updated ${ticket.number}`);
      } else {
        const result = await create.mutateAsync(form);
        toast.success(`Created ${result.number}`);
      }
      onClose();
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Save failed.');
    }
  }

  const submitting = create.isPending || update.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      kicker={editing ? 'Edit Ticket' : 'New Work Ticket'}
      title={editing ? `Edit ${ticket?.number}` : 'Create a work ticket.'}
      description={editing ? undefined : 'Tickets route through stations sequentially. Release to the floor when ready.'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" form="tkt-form" loading={submitting} leftIcon={<ClipboardList size={13} />}>
            {editing ? 'Save Changes' : 'Create Ticket'}
          </Button>
        </>
      }
    >
      <form id="tkt-form" onSubmit={onSubmit} className="space-y-5 py-2">
        {/* Source order */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Source Order"
            required
            value={form.orderId}
            onChange={(e) => update_('orderId', e.target.value)}
            options={[
              { value: '', label: '— Select an order —' },
              ...(orders ?? []).map((o) => ({
                value: o.id,
                label: `${o.number} · ${o.clientName ?? 'Unknown'}${o.projectName ? ` — ${o.projectName}` : ''}`,
              })),
            ]}
          />
          <Select
            label="Priority"
            value={form.priority}
            onChange={(e) => update_('priority', e.target.value as Priority)}
            options={Object.entries(PRIORITY_LABELS).map(([v, l]) => ({ value: v, label: l }))}
          />
        </div>

        <Input
          label="Description"
          required
          placeholder="e.g. Walnut quartered panels — Lobby"
          value={form.description}
          onChange={(e) => update_('description', e.target.value)}
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="Quantity"
            type="number" min="0" step="any"
            required
            value={form.quantity || ''}
            onChange={(e) => update_('quantity', Number(e.target.value) || 0)}
          />
          <Select
            label="Unit"
            value={form.unit ?? 'ea'}
            onChange={(e) => update_('unit', e.target.value)}
            options={[
              { value: 'ea',   label: 'each' },
              { value: 'sf',   label: 'sq ft' },
              { value: 'lf',   label: 'linear ft' },
              { value: 'pair', label: 'pair' },
              { value: 'set',  label: 'set' },
            ]}
          />
          <Input
            label="Due Date"
            type="date"
            value={form.dueDate?.slice(0, 10) ?? ''}
            onChange={(e) => update_('dueDate', e.target.value)}
          />
        </div>

        {/* Specs */}
        <div>
          <div className="kicker mb-3">Specifications (optional)</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Veneer Species"
              placeholder="Walnut, White Oak…"
              value={form.specs?.veneerSpecies ?? ''}
              onChange={(e) => update_('specs', { ...form.specs, veneerSpecies: e.target.value })}
            />
            <Input
              label="Cut"
              placeholder="Quarter sliced"
              value={form.specs?.cut ?? ''}
              onChange={(e) => update_('specs', { ...form.specs, cut: e.target.value })}
            />
            <Input
              label="Finish"
              placeholder="Perfecta UV Satin"
              value={form.specs?.finish ?? ''}
              onChange={(e) => update_('specs', { ...form.specs, finish: e.target.value })}
            />
            <Input
              label="Dimensions"
              placeholder={"4' × 8' × 3/4\""}
              value={form.specs?.dimensions ?? ''}
              onChange={(e) => update_('specs', { ...form.specs, dimensions: e.target.value })}
            />
          </div>
        </div>

        {/* Routing */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="kicker mb-1">Station Routing</div>
              <p className="text-2xs text-text-dim">
                Sequential order. Ticket advances through each station before completing.
              </p>
            </div>
            <Button size="sm" variant="secondary" leftIcon={<Plus size={12} />} type="button" onClick={addStep}>
              Add Step
            </Button>
          </div>

          <div className="space-y-2">
            {form.routing.map((step, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-bg-subtle border border-border rounded-sm p-2">
                <span className="text-2xs kicker w-6 text-center tabular">{String(idx + 1).padStart(2, '0')}</span>
                <div className="flex-1">
                  <Select
                    value={step.station}
                    onChange={(e) => {
                      const next = [...form.routing];
                      next[idx] = { ...step, station: e.target.value as Station };
                      update_('routing', next);
                    }}
                    options={STATIONS.map((s) => ({ value: s, label: STATION_LABELS[s] }))}
                  />
                </div>
                <div className="w-24">
                  <Input
                    type="number" min="0" step="1"
                    placeholder="Est. min"
                    value={step.estimatedMinutes ?? ''}
                    onChange={(e) => {
                      const next = [...form.routing];
                      next[idx] = {
                        ...step,
                        estimatedMinutes: e.target.value === '' ? undefined : Number(e.target.value),
                      };
                      update_('routing', next);
                    }}
                  />
                </div>
                <button type="button" onClick={() => moveStep(idx, -1)} disabled={idx === 0}
                  className="p-1.5 text-text-dim hover:text-text disabled:opacity-30 rounded-sm">
                  <ArrowUp size={12} />
                </button>
                <button type="button" onClick={() => moveStep(idx, 1)} disabled={idx === form.routing.length - 1}
                  className="p-1.5 text-text-dim hover:text-text disabled:opacity-30 rounded-sm">
                  <ArrowDown size={12} />
                </button>
                <button type="button" onClick={() => removeStep(idx)} disabled={form.routing.length <= 1}
                  className="p-1.5 text-text-dim hover:text-danger hover:bg-danger/10 disabled:opacity-30 rounded-sm">
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <Textarea
          label="Notes"
          rows={3}
          placeholder="Special instructions for the floor."
          value={form.notes ?? ''}
          onChange={(e) => update_('notes', e.target.value)}
        />

        {error && (
          <div className="text-xs text-danger border-l-2 border-danger pl-3 py-1">{error}</div>
        )}
      </form>
    </Modal>
  );
}

function hydrate(t: WorkTicket): SaveTicketInput {
  return {
    status: t.status, priority: t.priority,
    orderId: t.orderId, orderNumber: t.orderNumber,
    lineItemId: t.lineItemId,
    clientId: t.clientId, clientName: t.clientName, projectName: t.projectName,
    description: t.description, quantity: t.quantity, unit: t.unit,
    specs: t.specs,
    dueDate: t.dueDate?.slice(0, 10),
    routing: t.routing.map((r) => ({ station: r.station, estimatedMinutes: r.estimatedMinutes, notes: r.notes })),
    currentStepIndex: t.currentStepIndex,
    notes: t.notes,
  };
}
